import { useEffect, useState } from 'react';
import { Autocomplete, Avatar, Box, CircularProgress, TextField, Typography } from '@mui/material';

export interface StediPayerSearchOption {
  avatarUrl?: string | null;
  displayName: string;
  payerId?: string | null;
  stediId: string;
}

export interface StediPayerSearchProps {
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  inputValue?: string;
  label?: string;
  loading?: boolean;
  onInputChange: (value: string) => void;
  onSelect: (payer: StediPayerSearchOption | null) => void;
  options: StediPayerSearchOption[];
  placeholder?: string;
  value?: StediPayerSearchOption | null;
}

/** Contract-agnostic STEDI payer picker shared by provider and admin routes. */
export function StediPayerSearch({
  disabled = false,
  error = false,
  helperText,
  inputValue: controlledInputValue,
  label = 'Search STEDI payers',
  loading = false,
  onInputChange,
  onSelect,
  options,
  placeholder = 'Search by payer name or Payer ID',
  value = null,
}: StediPayerSearchProps) {
  const [inputValue, setInputValue] = useState('');
  const displayedInputValue = controlledInputValue ?? inputValue;
  const hasSearch = displayedInputValue.trim().length >= 2;

  useEffect(() => {
    if (controlledInputValue === undefined) setInputValue(value?.displayName ?? '');
  }, [controlledInputValue, value]);

  return (
    <Autocomplete
      disabled={disabled}
      filterOptions={items => items}
      getOptionLabel={option => option.displayName}
      inputValue={displayedInputValue}
      isOptionEqualToValue={(option, selected) => option.stediId === selected.stediId}
      loading={loading}
      loadingText="Searching STEDI payers…"
      noOptionsText={
        hasSearch ? 'No matching payers found' : 'Type at least 2 characters to search'
      }
      onChange={(_event, option) => onSelect(option)}
      onInputChange={(_event, nextValue, reason) => {
        if (controlledInputValue === undefined) setInputValue(nextValue);
        if (reason === 'input' || reason === 'clear') onInputChange(nextValue);
      }}
      options={options}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={key} {...optionProps}>
            {option.avatarUrl && (
              <Avatar
                src={option.avatarUrl}
                alt=""
                variant="rounded"
                sx={{ width: 24, height: 24, mr: 1 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.875rem' }}>{option.displayName}</Typography>
              {option.payerId && (
                <Typography variant="caption" color="text.secondary">
                  Payer ID {option.payerId}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          error={error}
          helperText={
            error
              ? 'Payers could not be loaded. Try your search again.'
              : displayedInputValue.length === 1
                ? 'Type at least 2 characters to search'
                : helperText
          }
          label={label}
          placeholder={placeholder}
          size="small"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={18} />}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      value={value}
    />
  );
}
